import React, { useState, useEffect } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { Site } from '../../../../types/site';
import { Publisher } from '../../../../types/publisher';
import { cnpjMask, validaCNPJ } from '../../../../utils';
import { createSite, updateSite } from '../../../../api/siteApi';
import { getPublishers } from '../../../../api/publisherApi';
import { useScopes } from '../../../../hooks/useScopes';
import { ApiScope } from '../../../../types/api-scopes';
import Select from 'react-select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';

// Custom styles for react-select
const customSelectStyles = {
    control: (provided: any, state: any) => ({
        ...provided,
        minHeight: '38px',
        borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
        boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none',
        '&:hover': {
            borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
        },
    }),
    placeholder: (provided: any) => ({
        ...provided,
        color: '#6c757d',
    }),
    singleValue: (provided: any) => ({
        ...provided,
        color: '#212529',
    }),
};

interface AddModalProps {
    site: Site | null;
    isOpen: boolean;
    toggle: () => void;
    onAddData: (newData: Site) => void;
    onUpdateData: (newData: Site) => void;
}

const AddEditSiteModal = ({ site, isOpen, toggle, onAddData, onUpdateData }: AddModalProps) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [publishers, setPublishers] = useState<Publisher[]>([]);
    const [mediaKitFile, setMediaKitFile] = useState<File | null>(null);
    const { userProfile, hasScope } = useScopes();

    // Convert publishers to react-select options
    const publisherOptions = publishers.map(publisher => ({
        value: publisher.id,
        label: publisher.name
    }));

    useEffect(() => {
        // Load publishers if user is not ROLE_PUBLISHER_ADMIN
        if (!userProfile?.publisher) {
            getPublishers().then(setPublishers).catch(console.error);
        }
    }, []);

    const toggleAddModal = () => {
        toggle();
    };

    const handleForm: any = useFormik({
        enableReinitialize: true,

        initialValues: {
            id: site?.id ? site.id : '',
            publisherId: site?.publisherId ? site.publisherId : '',
            mediaCad: site?.mediaCad ? site.mediaCad : '',
            document: site?.document ? site.document : '',
            site: site?.site ? site.site : '',
            app: site?.app ? site.app : '',
            mediaKitUrl: site?.mediaKitUrl ? site.mediaKitUrl : '',
            mediaKitFile: site?.mediaKitFile ? site.mediaKitFile : '',
            accessibilityResources: site?.accessibilityResources ? site.accessibilityResources : false,
        },
        validationSchema: Yup.object({
            publisherId: !userProfile?.publisher
                ? Yup.number().required('Publisher é obrigatório')
                : Yup.number().optional(),
            mediaCad: Yup.number().integer().optional(),
            document: Yup.string()
                .optional()
                .test('is-valid-cnpj', 'CNPJ inválido', value => value ? validaCNPJ(value) : true),
            site: Yup.string()
                .test(
                    'site-or-app',
                    'O campo site ou o campo app deve ser preenchido',
                    function (value) {
                        const { app } = this.parent;
                        return (!value && app) || (value && !app);
                    },
                )
                .test(
                    'site',
                    'Site inválido. Deve ser um domínio válido (ex: example.com)',
                    function (value: any) {
                        if (!value) {
                            return true;
                        }
                        try {
                            new URL(value.startsWith('http') ? value : `https://${value}`);
                            return true;
                        } catch {
                            return false;
                        }
                    },
                ),
            app: Yup.string()
                .test(
                    'app-or-site',
                    'O campo site ou o campo app deve ser preenchido',
                    function (value) {
                        const { site } = this.parent;
                        return (!value && site) || (value && !site);
                    },
                )
                .test(
                    'app',
                    'O ID do aplicativo deve ser um ID válido da Apple App Store (ex: id1234567890) ou Google Play Store (ex: com.example.app)',
                    function (value: any) {
                        if (!value) {
                            return true;
                        }
                        const appleAppStoreRegex = /^(id)?\d+$/;
                        const googlePlayStoreRegex = /^[a-zA-Z0-9._-]+$/;
                        return appleAppStoreRegex.test(value) || googlePlayStoreRegex.test(value);
                    },
                ),
            mediaKitUrl: Yup.string()
                .nullable()
                .optional()
                .url('URL inválida')
                .test(
                    'media-kit-exclusive',
                    'Apenas um tipo de media kit pode ser fornecido (URL ou arquivo)',
                    function (value) {
                        const { mediaKitFile } = this.parent;
                        return !(value && mediaKitFile);
                    }
                ),
            mediaKitFile: Yup.mixed()
                .nullable()
                .optional()
                .test(
                    'media-kit-exclusive',
                    'Apenas um tipo de media kit pode ser fornecido (URL ou arquivo)',
                    function (value) {
                        const { mediaKitUrl } = this.parent;
                        return !(value && mediaKitUrl);
                    }
                ),
            accessibilityResources: Yup.boolean().required('Recursos de acessibilidade é obrigatório'),
        }),
        onSubmit: (values: any) => {
            setLoading(true);

            if (site) {
                // For update, we need to use the site object with the id
                const siteData = {
                    ...site,
                    ...values,
                    mediaKitFile: mediaKitFile
                };
                updateSite(siteData)
                    .then((res: any) => {
                        toggleAddModal();
                        toast.success('Site atualizado com sucesso!');
                        onUpdateData(res);
                    })
                    .catch(() => { })
                    .finally(() => {
                        setLoading(false);
                    });
            } else {
                // For create, we need to provide the required fields
                const siteData = {
                    publisherId: values.publisherId,
                    publisher: { id: values.publisherId } as any,
                    mediaCad: values.mediaCad,
                    document: values.document,
                    site: values.site,
                    app: values.app,
                    mediaKitUrl: values.mediaKitUrl,
                    mediaKitFile: mediaKitFile ? mediaKitFile.name : undefined,
                    accessibilityResources: values.accessibilityResources,
                    placements: [],
                    orders: [],
                };
                createSite(siteData)
                    .then((res: any) => {
                        toggleAddModal();
                        toast.success('Site cadastrado com sucesso!');
                        onAddData(res);
                    })
                    .catch(() => { })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        },
    });

    return (
        <Dialog open={isOpen} onOpenChange={toggleAddModal}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{site ? 'Editar' : 'Adicionar'} Site</DialogTitle>
                </DialogHeader>

                <form onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleForm.handleSubmit();
                    return false;
                }} className="space-y-4">
                    {!userProfile?.publisher && (
                        <div className="space-y-2">
                            <Label htmlFor="publisherId">
                                Publisher *
                            </Label>
                            <Select
                                options={publisherOptions}
                                value={publisherOptions.find(option => option.value === handleForm.values.publisherId)}
                                onChange={(selectedOption) => {
                                    handleForm.setFieldValue('publisherId', selectedOption?.value || '');
                                }}
                                onBlur={() => handleForm.setFieldTouched('publisherId', true)}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                placeholder="Selecione um publisher"
                                isClearable
                                styles={customSelectStyles}
                            />
                            {handleForm.touched.publisherId && handleForm.errors.publisherId && (
                                <p className="text-sm text-red-500">{handleForm.errors.publisherId}</p>
                            )}
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="mediaCad">
                                Media CAD
                            </Label>
                            <Input
                                type="number"
                                id="mediaCad"
                                name="mediaCad"
                                value={handleForm.values.mediaCad}
                                onChange={handleForm.handleChange}
                                onBlur={handleForm.handleBlur}
                                placeholder="Digite o Media CAD"
                            />
                            {handleForm.touched.mediaCad && handleForm.errors.mediaCad && (
                                <p className="text-sm text-red-500">{handleForm.errors.mediaCad}</p>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="site">
                                Site
                            </Label>
                            <Input
                                type="text"
                                id="site"
                                name="site"
                                value={handleForm.values.site}
                                onChange={handleForm.handleChange}
                                onBlur={handleForm.handleBlur}
                                placeholder="example.com"
                            />
                            {handleForm.touched.site && handleForm.errors.site && (
                                <p className="text-sm text-red-500">{handleForm.errors.site}</p>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="app">
                                App
                            </Label>
                            <Input
                                type="text"
                                id="app"
                                name="app"
                                value={handleForm.values.app}
                                onChange={handleForm.handleChange}
                                onBlur={handleForm.handleBlur}
                                placeholder="id1234567890 ou com.example.app"
                            />
                            {handleForm.touched.app && handleForm.errors.app && (
                                <p className="text-sm text-red-500">{handleForm.errors.app}</p>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="mediaKitUrl">
                                Media Kit URL
                            </Label>
                            <Input
                                type="url"
                                id="mediaKitUrl"
                                name="mediaKitUrl"
                                value={handleForm.values.mediaKitUrl}
                                onChange={(e) => {
                                    handleForm.setFieldValue('mediaKitUrl', e.target.value);
                                    if (e.target.value) {
                                        handleForm.setFieldValue('mediaKitFile', '');
                                        setMediaKitFile(null);
                                    }
                                }}
                                onBlur={handleForm.handleBlur}
                                placeholder="https://example.com/media-kit.pdf"
                            />
                            {handleForm.touched.mediaKitUrl && handleForm.errors.mediaKitUrl && (
                                <p className="text-sm text-red-500">{handleForm.errors.mediaKitUrl}</p>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="mediaKitFile">
                                Media Kit Arquivo
                            </Label>
                            <Input
                                type="file"
                                id="mediaKitFile"
                                name="mediaKitFile"
                                onChange={(e) => {
                                    const file = e.target.files?.[0] || null;
                                    setMediaKitFile(file);
                                    handleForm.setFieldValue('mediaKitFile', file);
                                    if (file) {
                                        handleForm.setFieldValue('mediaKitUrl', '');
                                    }
                                }}
                                onBlur={handleForm.handleBlur}
                                accept=".pdf,.doc,.docx"
                            />
                            {handleForm.touched.mediaKitFile && handleForm.errors.mediaKitFile && (
                                <p className="text-sm text-red-500">{handleForm.errors.mediaKitFile}</p>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="document">
                                Documento (CNPJ)
                            </Label>
                            <Input
                                type="text"
                                id="document"
                                name="document"
                                value={cnpjMask(handleForm.values.document)}
                                onChange={handleForm.handleChange}
                                onBlur={handleForm.handleBlur}
                                placeholder="00.000.000/0000-00"
                            />
                            {handleForm.touched.document && handleForm.errors.document && (
                                <p className="text-sm text-red-500">{handleForm.errors.document}</p>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="accessibilityResources">
                                Recursos de Acessibilidade *
                            </Label>
                            <div className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    id="accessibilityResources"
                                    name="accessibilityResources"
                                    checked={handleForm.values.accessibilityResources}
                                    onChange={(e) => handleForm.setFieldValue('accessibilityResources', e.target.checked)}
                                    onBlur={handleForm.handleBlur}
                                    className="w-4 h-4"
                                />
                                <Label htmlFor="accessibilityResources" className="cursor-pointer">
                                    {handleForm.values.accessibilityResources ? 'Ativado' : 'Desativado'}
                                </Label>
                            </div>
                            {handleForm.touched.accessibilityResources && handleForm.errors.accessibilityResources && (
                                <p className="text-sm text-red-500">{handleForm.errors.accessibilityResources}</p>
                            )}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button type="submit" disabled={loading} className="flex items-center gap-2">
                            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                            {site ? 'Editar' : 'Cadastrar'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default AddEditSiteModal;
